import { ApiClientError } from '@/shared/lib/api';
import {
  BaseApiService,
  type ServiceOptions,
} from '@/shared/api/services/base.service';
import { buildUrl, type FilterValue } from '@/shared/lib/query-params';
import { noteBuildingsListed } from '@/shared/lib/deleted-buildings';
import type {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  JsonApiResponse,
  PaginationParams,
  SortParams,
  FilterParams,
  IncludeParams,
} from '@/types/project';
import type { Concept } from '@/types/concept';
import type { ProjectAngle } from '@/types/project-angle';

interface ProjectsListParams
  extends PaginationParams,
    SortParams,
    FilterParams,
    IncludeParams {}

class ProjectsService extends BaseApiService {
  async list(params?: ProjectsListParams): Promise<JsonApiResponse<Project[]>> {
    const url = buildUrl('/v1/projects', {
      include: params?.include,
      sort: params?.sort,
      filter: params?.filter as Record<string, FilterValue> | undefined,
      page:
        params?.number || params?.size
          ? { number: params?.number, size: params?.size }
          : undefined,
    });
    const requestedAt = Date.now();
    const response = await this.httpGet<JsonApiResponse<Project[]>>(url);
    // A building listed again was restored (Undo in another tab, Trash).
    noteBuildingsListed(
      response.data.map((project) => project.id),
      requestedAt
    );
    return response;
  }

  async create(name: string): Promise<JsonApiResponse<Project>> {
    const request: CreateProjectRequest = {
      data: {
        type: 'project',
        attributes: { name },
      },
    };
    return this.post<JsonApiResponse<Project>>('/v1/projects', request);
  }

  async get(
    projectId: string,
    options?: ServiceOptions
  ): Promise<JsonApiResponse<Project>> {
    return this.httpGet<JsonApiResponse<Project>>(
      `/v1/projects/${projectId}`,
      undefined,
      options
    );
  }

  // `keepalive` lets the PATCH survive page unload / hard navigation — used by
  // the goals page on-unmount flush so fast back-press doesn't strand unsent edits.
  async update(
    projectId: string,
    attributes: Partial<UpdateProjectRequest['data']['attributes']>,
    options?: { keepalive?: boolean }
  ): Promise<JsonApiResponse<Project>> {
    const request: UpdateProjectRequest = {
      data: {
        type: 'project',
        attributes,
      },
    };
    return this.put<JsonApiResponse<Project>>(
      `/v1/projects/${projectId}`,
      request,
      options?.keepalive ? { keepalive: true } : undefined
    );
  }

  /**
   * Mint (or fetch) the CONSTRUCTED view of a building that has never been
   * photographed. Idempotent per side, so a retried "Set up design" can't
   * double the view list; the server refuses it (422) for a building that
   * exists, where a photo is available and strictly better.
   *
   * `viewpoint` is omitted here on purpose: the server picks the brief's main
   * facade, falling back to the street side. The client has no better answer
   * and guessing one would contradict the brief.
   */
  async createConstructedView(
    projectId: string
  ): Promise<{ data: ProjectAngle }> {
    return this.post<{ data: ProjectAngle }>(
      `/v1/projects/${projectId}/views`,
      { data: { type: 'angle' } }
    );
  }

  async delete(projectId: string): Promise<void> {
    try {
      await this.httpDelete(`/v1/projects/${projectId}`);
    } catch (error) {
      // 404 on DELETE means the building is already gone (a timed-out first
      // attempt the server committed, or another device). That is success;
      // surfacing it as a failure keeps a zombie building around (APPLE-19).
      if (error instanceof ApiClientError && error.isNotFoundError()) return;
      throw error;
    }
  }

  async restore(projectId: string): Promise<void> {
    await this.post(`/v1/projects/${projectId}/restore`);
  }

  async listConcepts(
    projectId: string,
    params?: ProjectsListParams
  ): Promise<JsonApiResponse<Concept[]>> {
    const url = buildUrl(`/v1/projects/${projectId}/concepts`, {
      include: params?.include,
      sort: params?.sort,
      filter: params?.filter as Record<string, FilterValue> | undefined,
      page:
        params?.number || params?.size
          ? { number: params?.number, size: params?.size }
          : undefined,
    });
    return this.httpGet<JsonApiResponse<Concept[]>>(url);
  }
}

export const projectsService = new ProjectsService();
